import React, { useState, useRef, useEffect } from 'react';
import { aiAPI } from '../services/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

function AIChatAssistant({ assetId, chapterId, isDarkMode = false, assignmentContext = null, isOpen: controlledIsOpen, onOpenChange }) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  
  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = onOpenChange || setInternalIsOpen;
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [shouldScrollToAnswer, setShouldScrollToAnswer] = useState(false);
  const [shouldScrollToQuestion, setShouldScrollToQuestion] = useState(false);
  const [explainLevel, setExplainLevel] = useState(() => {
    // Load preference from localStorage
    return localStorage.getItem('aiExplainLevel') || 'brief';
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const prevIsOpenRef = useRef(false);
  const lastAssistantMessageRef = useRef(null);
  const lastUserMessageRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToLastAssistantMessage = () => {
    if (lastAssistantMessageRef.current) {
      lastAssistantMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToUserQuestion = () => {
    if (lastUserMessageRef.current) {
      lastUserMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    if (shouldScrollToAnswer) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        scrollToLastAssistantMessage();
        setShouldScrollToAnswer(false);
      }, 100);
    } else if (shouldScrollToQuestion) {
      // Scroll user question to top of viewport
      setTimeout(() => {
        scrollToUserQuestion();
        setShouldScrollToQuestion(false);
      }, 100);
    }
  }, [messages, shouldScrollToAnswer, shouldScrollToQuestion]);

  useEffect(() => {
    // Save preference to localStorage
    localStorage.setItem('aiExplainLevel', explainLevel);
  }, [explainLevel]);

  useEffect(() => {
    if (isOpen) {
      if (inputRef.current) {
        inputRef.current.focus();
      }
      // Scroll to bottom when chatbot opens
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [isOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Check for Ctrl + Shift combinations
      if (e.ctrlKey && e.shiftKey) {
        switch(e.key.toLowerCase()) {
          case 'o': // Open chat
            e.preventDefault();
            setIsOpen(true);
            break;
          case 'c': // Close chat
            e.preventDefault();
            setIsOpen(false);
            break;
          case 'm': // Maximize chat
            e.preventDefault();
            if (isOpen) {
              setIsExpanded(true);
            } else {
              setIsOpen(true);
              setIsExpanded(true);
            }
            break;
          case 'd': // Default/Minimize chat
            e.preventDefault();
            if (isOpen) {
              setIsExpanded(false);
            } else {
              setIsOpen(true);
              setIsExpanded(false);
            }
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, setIsOpen, setIsExpanded]);

  // Fetch suggested questions every time chatbot opens
  useEffect(() => {
    const fetchSuggestedQuestions = async () => {
      if (!assetId && !assignmentContext) {
        console.log('No assetId or assignmentContext, skipping suggestions');
        return;
      }
      
      try {
        console.log('Fetching suggested questions...', { assetId, chapterId, assignmentContext });
        setLoadingSuggestions(true);
        const response = await aiAPI.suggestQuestions({ 
          assetId, 
          chapterId,
          ...(assignmentContext && { assignmentContext })
        });
        console.log('Received suggested questions:', response.data.questions);
        // Replace with new suggestions (don't append to old ones)
        setSuggestedQuestions(response.data.questions || []);
      } catch (error) {
        console.error('Error fetching suggested questions:', error);
      } finally {
        setLoadingSuggestions(false);
      }
    };

    // Check if chatbot just opened (transitioned from closed to open)
    const justOpened = isOpen && !prevIsOpenRef.current;
    const justClosed = !isOpen && prevIsOpenRef.current;
    
    if (justOpened) {
      console.log('Chatbot just opened, fetching suggestions');
      // Show suggestions and fetch fresh suggestions when chatbot opens
      setShowSuggestions(true);
      fetchSuggestedQuestions();
    }
    
    if (justClosed) {
      console.log('Chatbot closed');
      // Don't clear suggestions when closing, just hide them
    }
    
    // Update the previous state
    prevIsOpenRef.current = isOpen;
  }, [isOpen, assetId, chapterId, assignmentContext]);

  // Clear messages when content (asset/chapter) changes
  useEffect(() => {
    setMessages([]);
  }, [assetId, chapterId]);

  const handleSuggestedQuestionClick = async (question) => {
    if (isLoading) return;

    const userMessage = {
      role: 'user',
      content: question,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setShowSuggestions(false); // Hide suggestions once user clicks one
    setShouldScrollToQuestion(true); // Scroll to the question
    setIsLoading(true);

    try {
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await aiAPI.ask({
        question: question,
        assetId,
        chapterId,
        conversationHistory,
        wasSuggested: true,
        explainLevel,
        ...(assignmentContext && { assignmentContext })
      });

      const aiMessage = {
        role: 'assistant',
        content: response.data.answer,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('AI chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your question. Please try again.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    setShouldScrollToQuestion(true); // Scroll to the question
    setIsLoading(true);

    try {
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await aiAPI.ask({
        question: userMessage.content,
        assetId,
        chapterId,
        conversationHistory,
        wasSuggested: false,
        explainLevel,
        ...(assignmentContext && { assignmentContext })
      });

      const aiMessage = {
        role: 'assistant',
        content: response.data.answer,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('AI chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your question. Please try again.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  return (
    <>
      {/* Floating Button */}
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setIsOpen(true)}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            fontSize: '28px',
            boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            transition: 'transform 0.2s ease'
          }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          title="Ask AI Assistant"
        >
          🤖
        </button>

        {/* Tooltip */}
        {showTooltip && (
          <div style={{
            position: 'fixed',
            bottom: '90px',
            right: '24px',
            background: '#1a202c',
            color: '#fff',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '13px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            zIndex: 10000,
            minWidth: '220px',
            lineHeight: '1.6'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.2)', paddingBottom: '6px' }}>
              ⌨️ Keyboard Shortcuts
            </div>
            <div style={{ fontSize: '12px' }}>
              <div style={{ marginBottom: '4px' }}><strong>Ctrl+Shift+O</strong> - Open</div>
              <div style={{ marginBottom: '4px' }}><strong>Ctrl+Shift+C</strong> - Close</div>
              <div style={{ marginBottom: '4px' }}><strong>Ctrl+Shift+M</strong> - Maximize</div>
              <div><strong>Ctrl+Shift+D</strong> - Default size</div>
            </div>
          </div>
        )}
      </div>

      {/* Modal Overlay */}
      {isOpen && (
        <>
          {/* Backdrop for expanded mode */}
          {isExpanded && (
            <div
              onClick={() => setIsOpen(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                zIndex: 9998
              }}
            />
          )}
          
          {/* Modal Container */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: isExpanded ? 'center' : 'flex-end',
              justifyContent: isExpanded ? 'center' : 'flex-end',
              zIndex: 9999,
              padding: isExpanded ? '20px' : '0',
              pointerEvents: 'none'
            }}
          >
          {/* Modal Content */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: isExpanded ? '100%' : '420px',
              maxWidth: isExpanded ? '1000px' : '420px',
              height: isExpanded ? '90vh' : '650px',
              maxHeight: isExpanded ? '800px' : '650px',
              background: isDarkMode ? '#1a202c' : '#ffffff',
              borderRadius: isExpanded ? '16px' : '16px 16px 0 0',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column',
              border: `1px solid ${isDarkMode ? '#2d3748' : '#e2e8f0'}`,
              marginRight: isExpanded ? '0' : '24px',
              marginBottom: isExpanded ? '0' : '0',
              transition: 'all 0.3s ease',
              pointerEvents: 'auto'
            }}
          >
            {/* Header */}
            <div
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                padding: '20px 24px',
                borderRadius: '16px 16px 0 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '32px' }}>🤖</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>AI Assistant</h3>
                  <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>Ask me anything about this content</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    cursor: 'pointer',
                    padding: '8px 14px',
                    fontSize: '20px',
                    lineHeight: '1',
                    fontWeight: 'bold'
                  }}
                  title={isExpanded ? 'Minimize' : 'Maximize'}
                >
                  {isExpanded ? '−' : '+'}
                </button>
                {messages.length > 0 && (
                  <button
                    onClick={clearChat}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      cursor: 'pointer',
                      padding: '8px 14px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    title="Clear conversation"
                  >
                    🗑️ Clear
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    cursor: 'pointer',
                    padding: '8px 14px',
                    fontSize: '20px',
                    lineHeight: '1'
                  }}
                  title="Close chat"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Messages */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px'
              }}
            >
            {messages.length === 0 && (
              <div style={{ 
                textAlign: 'center', 
                color: isDarkMode ? '#a0aec0' : '#718096',
                padding: '60px 40px 40px'
              }}>
                <p style={{ fontSize: '64px', margin: '0 0 20px 0' }}>👋</p>
                <p style={{ fontSize: '18px', margin: 0, fontWeight: '500' }}>
                  Hi! I'm here to help you understand this content better.
                  <br />Ask me any questions!
                </p>
              </div>
            )}

            {messages.map((msg, idx) => {
              const isLastAssistantMessage = msg.role === 'assistant' && 
                idx === messages.length - 1;
              const isLastUserMessage = msg.role === 'user' && 
                idx === messages.length - 1;
              
              return (
                <div
                  key={idx}
                  ref={isLastAssistantMessage ? lastAssistantMessageRef : isLastUserMessage ? lastUserMessageRef : null}
                  style={{
                    alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '75%'
                  }}
                >
                  {msg.role === 'assistant' && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontSize: '24px' }}>🤖</span>
                      <span style={{ 
                        fontSize: '14px', 
                        color: isDarkMode ? '#a0aec0' : '#718096',
                        fontWeight: '600'
                      }}>
                        AI Assistant
                      </span>
                    </div>
                  )}
                  <div
                    style={{
                      padding: '16px 20px',
                      borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      background: msg.role === 'user' 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : msg.isError
                        ? (isDarkMode ? '#742a2a' : '#fed7d7')
                        : (isDarkMode ? '#2d3748' : '#f7fafc'),
                      color: msg.role === 'user' ? '#fff' : (isDarkMode ? '#e2e8f0' : '#1a202c'),
                      fontSize: '16px',
                      lineHeight: '1.7'
                    }}
                  >
                    {msg.role === 'assistant' ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({node, ...props}) => <h1 style={{ fontSize: '22px', margin: '12px 0', color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props} />,
                          h2: ({node, ...props}) => <h2 style={{ fontSize: '20px', margin: '10px 0', color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props} />,
                          h3: ({node, ...props}) => <h3 style={{ fontSize: '18px', margin: '8px 0', color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props} />,
                          p: ({node, ...props}) => <p style={{ margin: '8px 0', color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props} />,
                          ul: ({node, ...props}) => <ul style={{ margin: '8px 0', paddingLeft: '24px' }} {...props} />,
                          ol: ({node, ...props}) => <ol style={{ margin: '8px 0', paddingLeft: '24px' }} {...props} />,
                          li: ({node, ...props}) => <li style={{ margin: '6px 0', color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props} />,
                          a: ({node, ...props}) => <a style={{ color: isDarkMode ? '#90cdf4' : '#667eea', textDecoration: 'underline' }} {...props} />,
                          strong: ({...props}) => <strong style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props} />,
                          code({ node, inline, className, children, ...props }) {
                            const match = /language-(\w+)/.exec(className || '');
                            return !inline && match ? (
                              <SyntaxHighlighter
                                style={vscDarkPlus}
                                language={match[1]}
                                PreTag="div"
                                customStyle={{ fontSize: '14px', margin: '12px 0', borderRadius: '8px' }}
                                {...props}
                              >
                                {String(children).replace(/\n$/, '')}
                              </SyntaxHighlighter>
                            ) : (
                              <code style={{ 
                                background: isDarkMode ? '#1a202c' : '#e2e8f0',
                                padding: '3px 8px',
                                borderRadius: '4px',
                                fontSize: '14px',
                                color: isDarkMode ? '#e2e8f0' : '#1a202c'
                              }} {...props}>
                                {children}
                              </code>
                            );
                          }
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                    ) : (
                      msg.content
                    )}
                  </div>
                  {msg.role === 'user' && (
                    <div style={{ 
                      fontSize: '12px', 
                      color: isDarkMode ? '#718096' : '#a0aec0',
                      textAlign: 'right',
                      marginTop: '6px'
                    }}>
                      You
                    </div>
                  )}
                </div>
              );
            })}

            {/* Suggested Questions - Shown when user hasn't clicked any yet */}
            {showSuggestions && loadingSuggestions && (
              <div style={{
                textAlign: 'center',
                padding: '20px',
                color: isDarkMode ? '#a0aec0' : '#718096',
                fontSize: '14px'
              }}>
                <span className="typing-indicator">
                  <span>.</span><span>.</span><span>.</span>
                </span>
                Generating suggested questions...
              </div>
            )}
            
            {showSuggestions && !loadingSuggestions && suggestedQuestions.length > 0 && (
              <div style={{ padding: '0 40px 20px' }}>
                <h4 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: isDarkMode ? '#a0aec0' : '#718096',
                  marginBottom: '12px',
                  textAlign: 'center'
                }}>
                  💡 Suggested Questions
                </h4>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px'
                }}>
                  {suggestedQuestions.map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSuggestedQuestionClick(question)}
                      disabled={isLoading}
                      style={{
                        padding: '14px 18px',
                        background: isDarkMode ? '#2d3748' : '#f7fafc',
                        border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                        borderRadius: '10px',
                        color: isDarkMode ? '#e2e8f0' : '#1a202c',
                        fontSize: '15px',
                        textAlign: 'left',
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        lineHeight: '1.5'
                      }}
                      onMouseOver={(e) => {
                        if (!isLoading) {
                          e.currentTarget.style.background = isDarkMode ? '#4a5568' : '#edf2f7';
                          e.currentTarget.style.borderColor = '#667eea';
                        }
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = isDarkMode ? '#2d3748' : '#f7fafc';
                        e.currentTarget.style.borderColor = isDarkMode ? '#4a5568' : '#e2e8f0';
                      }}
                    >
                      ❓ {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isLoading && (
              <div style={{ alignSelf: 'flex-start', maxWidth: '75%' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '8px'
                }}>
                  <span style={{ fontSize: '24px' }}>🤖</span>
                  <span style={{ 
                    fontSize: '14px', 
                    color: isDarkMode ? '#a0aec0' : '#718096',
                    fontWeight: '600'
                  }}>
                    AI Assistant
                  </span>
                </div>
                <div
                  style={{
                    padding: '16px 20px',
                    borderRadius: '16px 16px 16px 4px',
                    background: isDarkMode ? '#2d3748' : '#f7fafc',
                    color: isDarkMode ? '#a0aec0' : '#718096',
                    fontSize: '16px'
                  }}
                >
                  <span className="typing-indicator">
                    <span>.</span><span>.</span><span>.</span>
                  </span>
                  Thinking...
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form
            onSubmit={handleSubmit}
            style={{
              padding: '20px 24px',
              borderTop: `1px solid ${isDarkMode ? '#2d3748' : '#e2e8f0'}`,
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-end'
            }}
          >
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              placeholder="Ask a question... (Press Enter to send, Shift+Enter for new line)"
              disabled={isLoading}
              rows={1}
              style={{
                flex: 1,
                padding: '14px 18px',
                border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                borderRadius: '10px',
                fontSize: '16px',
                outline: 'none',
                background: isDarkMode ? '#2d3748' : '#ffffff',
                color: isDarkMode ? '#e2e8f0' : '#1a202c',
                resize: 'none',
                fontFamily: 'inherit',
                lineHeight: '1.5',
                minHeight: '50px',
                maxHeight: '150px',
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = isDarkMode ? '#4a5568' : '#e2e8f0'}
              onInput={(e) => {
                // Auto-resize textarea based on content
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
              }}
            />
            <select
              value={explainLevel}
              onChange={(e) => setExplainLevel(e.target.value)}
              style={{
                padding: '14px 16px',
                background: isDarkMode ? '#2d3748' : '#ffffff',
                border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                borderRadius: '10px',
                color: isDarkMode ? '#e2e8f0' : '#1a202c',
                fontSize: '15px',
                fontWeight: '500',
                cursor: 'pointer',
                outline: 'none',
                minWidth: '130px',
                alignSelf: 'flex-end'
              }}
            >
              <option value="brief">💬 Brief</option>
              <option value="medium">📝 Medium</option>
              <option value="elaborate">📚 Elaborate</option>
            </select>
          </form>

          <style>{`
            .typing-indicator span {
              animation: blink 1.4s infinite;
              animation-fill-mode: both;
            }
            .typing-indicator span:nth-child(2) {
              animation-delay: 0.2s;
            }
            .typing-indicator span:nth-child(3) {
              animation-delay: 0.4s;
            }
            @keyframes blink {
              0%, 80%, 100% { opacity: 0; }
              40% { opacity: 1; }
            }
          `}</style>
        </div>
      </div>
      </>
      )}
    </>
  );
}

export default AIChatAssistant;
